// Dynamically load Forte scripts from URLs specified in environment variables
export function loadForteScripts() {
  if (typeof window === 'undefined') return Promise.resolve();

  // Return cached promise only if it succeeded (window.forte is set)
  if (window.__FORTE_LOADING__ && window.forte) return window.__FORTE_LOADING__;
  if (window.forte) return Promise.resolve();

  // If a previous attempt is still in-flight, wait for it
  if (window.__FORTE_LOADING__ && window.__FORTE_LOADING__._pending) {
    return window.__FORTE_LOADING__;
  }

  const checkoutSrc = process.env.REACT_APP_FORTE_CHECKOUT_URL;
  const apiSrc = process.env.REACT_APP_FORTE_API_URL;

  const host = (typeof window !== 'undefined' && window.location?.hostname) ? window.location.hostname : '';
  const isSandboxHost =
    host.includes('dev') ||
    host.includes('demo') ||
    host.includes('localhost') ||
    host.includes('127.0.0.1');

  const fallbackCheckoutSrc = isSandboxHost
    ? 'https://sandbox.forte.net/checkout/v2/js?nocache=2'
    : 'https://checkout.forte.net/v2/js?nocache=1';
  const fallbackApiSrc = isSandboxHost
    ? 'https://sandbox.forte.net/api/js/v1?nocache=1'
    : 'https://api.forte.net/js/v1?nocache=1';

  const resolvedCheckoutSrc = checkoutSrc || fallbackCheckoutSrc;
  const resolvedApiSrc = apiSrc || fallbackApiSrc;

  function addScript(src) {
    return new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset.forteLoaded === '1') {
          // Confirmed loaded by our own code — safe to skip
          return resolve();
        }
        // Any other state (failed, unknown, old code's tag) — remove and re-add fresh
        existing.parentNode.removeChild(existing);
      }
      const s = document.createElement('script');
      s.type = 'text/javascript';
      s.src = src;
      s.onload = () => { s.dataset.forteLoaded = '1'; resolve(); };
      s.onerror = () => { s.dataset.forteFailed = '1'; reject(new Error('forte_script_load_failed:' + src)); };
      document.head.appendChild(s);
    });
  }

  const p = Promise.resolve()
    .then(() => addScript(resolvedCheckoutSrc))
    .then(() => addScript(resolvedApiSrc))
    .then(() => {
      p._pending = false;
      return { loaded: true };
    })
    .catch((e) => {
      const failedSrc = e.message.startsWith('forte_script_load_failed:')
        ? e.message.slice('forte_script_load_failed:'.length)
        : '';
      window.__FORTE_FAILED_SCRIPT__ = failedSrc || e.message;
      console.error('[ForteScriptLoader] Failed to load Forte scripts', { error: e, failedSrc });
      // Clear the cache so the next call retries from scratch
      if (window.__FORTE_LOADING__ === p) delete window.__FORTE_LOADING__;
      p._pending = false;
      return { loaded: false, reason: 'script-load-failed', failedScript: failedSrc };
    });

  p._pending = true;
  window.__FORTE_LOADING__ = p;
  return p;
}

export function loadForteForCurrentEnv() {
  try {
    return Promise.resolve(loadForteScripts()).catch((e) => {
      console.error('[ForteScriptLoader] Suppressed Forte loader error', e);
      return { loaded: false, reason: 'loader-error' };
    });
  } catch (e) {
    console.error('[ForteScriptLoader] Suppressed sync Forte loader error', e);
    return Promise.resolve({ loaded: false, reason: 'loader-error' });
  }
}
